import type { ShallowRef } from 'vue';
import { orderBy } from 'lodash-es';
import { useStreamlabsFetch } from '~/lib/streamlabs-api';
import { openBrowserTo } from '~/lib/bindings';
import IconTrovo from '~/components/icons/IconTrovo.vue';
import IconTikTok from '~/components/icons/IconTikTok.vue';
import { equalityRef } from '~/lib/util';
import IconPatreon from '~/components/icons/IconPatreon.vue';

// todo: scenario -- user has multistream enabled via API, but OBS is not pointing to it
// TODO: it looks like this type doesn't actually have anything to do with chat support
export type MultistreamChatSupportedPlatform =
  | 'twitch'
  | 'youtube'
  | 'facebook'
  | 'tiktok'
  | 'twitter'
  | 'trovo'
  | 'kick'
  | 'patreon';

export enum RstToPlatformType {
  kick_account = 'kick',
  twitch_account = 'twitch',
  facebook_account = 'facebook',
  youtube_account = 'youtube',
  trovo_account = 'trovo',
  twitter_account = 'twitter',
  tiktok_account = 'tiktok',
  patreon_account = 'patreon',
}

export enum PlatformTypeToRst {
  kick = 'kick_account',
  twitch = 'twitch_account',
  facebook = 'facebook_account',
  youtube = 'youtube_account',
  trovo = 'trovo_account',
  twitter = 'twitter_account',
  tiktok = 'tiktok_account',
  patreon = 'patreon_account',
}

export const useMultistreamStore = defineStore(
  'multistream',
  () => {
    console.debug('multistream.store');
    const obs = useObsStore();
    const auth = useAuthStore();
    const api = useApiStore();
    const app = useAppStore();
    const dialogs = useDialogsStore();
    const tracking = useTrackingStore();
    const goLive = useGoLiveStore();

    const currentBroadcast = ref<MultistreamLive>();

    auth.socket.on('multistreamLive', (payload) => {
      console.debug('multistreamLive', payload);
      currentBroadcast.value = payload;

      setMultistreamExtraInfo(payload);
    });

    auth.socket.on('account_permissions_required', (e) => {
      for (const item of e.message) {
        auth.tryLogin(item.url).then(() => {
          const chatDock = obs.getDockByTitle('Streamlabs: Chat');

          if (chatDock) {
            chatDock.executeJavascript(`window.location.reload(true);`);
          }
        });
        break;
      }
    });

    const stopPreviousBroadcast = async () => {
      const data = currentBroadcast.value;

      if (!data) {
        return;
      }

      for (const { broadcast_id, platform } of Object.values(data)) {
        goLive.stopBroadcast(platform, broadcast_id || 'all');
      }
    };

    const enableMultistream = () => {
      return useStreamlabsFetch<RstUserSettingsResponse>(
        `/api/v5/obs-plugin/rst/user/settings`,
        {
          method: 'POST',
          body: {
            enabled: true,
          },
        },
      );
    };

    const disableMultistream = () => {
      return useStreamlabsFetch<RstUserSettingsResponse>(
        `/api/v5/obs-plugin/rst/user/settings/disable`,
        {
          method: 'POST',
        },
      );
    };

    const setMultistreamExtraInfo = (payload: MultistreamLive) => {
      const platforms = Object.keys(payload) as RstToPlatformType[];
      const destinationsWithLiveInfo = platforms.reduce((acc, current) => {
        const response = payload[current];
        const multistreamPlatform = destinations.value
          .filter((d) => d.type === 'platform')
          .find((platform) => platform.platform_id === response?.platform_id);

        if (response && multistreamPlatform) {
          const liveInfo = {};
          if (
            multistreamPlatform.platform_type ===
            RstToPlatformType.youtube_account
          ) {
            (liveInfo as YouTubeLiveInfo).broadcastId = response.broadcast_id;
          }
          // TODO: facebook
          return [
            ...acc,
            { ...multistreamPlatform, liveInfo },
          ] as DestinationPlatformWithLiveInfo[];
        }

        return acc;
      }, [] as DestinationPlatformWithLiveInfo[]);

      goLive.setAndBroadcastStreamingStatus(true, destinationsWithLiveInfo);
    };

    type PlatformConfig = {
      id: string;
      label: string;
      icon: string | ShallowRef;
      iconClass?: string;
      buttonClass?: string;
      switchClass?: string;
    };

    const platformConfigs = ref<Record<RstToPlatformType, PlatformConfig>>({
      kick: {
        id: 'kick',
        label: 'Kick',
        icon: 'simple-icons:kick',
        iconClass: '!text-kick',
        buttonClass: '!bg-kick !text-[#081902]',
        switchClass: `data-[state=checked]:!bg-kick`,
      },
      twitch: {
        id: 'twitch',
        label: 'Twitch',
        icon: 'simple-icons:twitch',
        iconClass: '!text-twitch',
        buttonClass: '!bg-twitch !text-black',
        switchClass: `data-[state=checked]:!bg-twitch`,
      },
      youtube: {
        id: 'youtube',
        label: 'YouTube',
        icon: 'simple-icons:youtube',
        iconClass: '!text-youtube',
        buttonClass: '!bg-youtube !text-white',
        switchClass: `data-[state=checked]:!bg-youtube`,
      },
      facebook: {
        id: 'facebook',
        label: 'Facebook',
        icon: 'simple-icons:facebook',
        iconClass: '!text-facebook',
        buttonClass: '!bg-facebook !text-white',
        switchClass: `data-[state=checked]:!bg-facebook`,
      },
      trovo: {
        id: 'trovo',
        label: 'Trovo',
        icon: shallowRef(IconTrovo),
        iconClass: '!text-trovo',
        buttonClass: '!bg-trovo !text-white',
        switchClass: `data-[state=checked]:!bg-trovo`,
      },
      twitter: {
        id: 'twitter',
        label: 'X (Twitter)',
        icon: 'simple-icons:x',
        iconClass: '!text-twitter',
        buttonClass: '!bg-twitter !text-black',
        switchClass: `data-[state=checked]:!bg-neutral-600`,
      },
      tiktok: {
        id: 'tiktok',
        label: 'TikTok',
        icon: shallowRef(IconTikTok),
        iconClass: '!text-tiktok',
        buttonClass: '!bg-tiktok !text-white',
        switchClass: 'data-[state=checked]:!bg-tiktok',
      },
      patreon: {
        id: 'patreon',
        label: 'Patreon',
        icon: shallowRef(IconPatreon),
        iconClass: '!text-patreon',
        buttonClass: '!bg-patreon !text-black',
        switchClass: 'data-[state=checked]!bg-patreon',
      },
    });

    const relays = computed(() => {
      return (api.multistream.user.targets.data || []).filter(
        (target) => target.platform === 'relay',
      );
    });

    const destinations = equalityRef<Destination[]>([]);

    watchEffect(() => {
      let items: Destination[] = [];

      if (
        api.multistream.user.platforms.data &&
        api.multistream.user.profile.data &&
        api.platformInfo.data
      ) {
        for (const [k, v] of Object.entries(
          api.multistream.user.platforms.data,
        ) as Entries<typeof api.multistream.user.platforms.data>) {
          const platformInfo = api.platformInfo.data.platform_info[k];
          const enabled = !!api.multistream.user.profile.data[k];
          const available =
            !v.available_for ||
            Object.values(v.available_for).includes('obs-plugin');

          items.push({
            type: 'platform',
            platform_type: RstToPlatformType[k],
            platform_username: platformInfo?.username,
            platform_id: platformInfo?.id,

            is_enabled: enabled,
            is_connected: v.connected,
            has_permission: !!v.has_permission,
            no_permission_reason: v.reason ?? undefined,

            enabled,
            connected: v.connected,
            valid: !!v.has_permission,
            available,
            order: ['twitch', 'tiktok'].reverse().indexOf(RstToPlatformType[k]),
          });
        }
      }

      items = orderBy(
        items,
        ['connected', 'valid', 'order'],
        ['desc', 'desc', 'desc'],
      );

      for (const relay of relays.value) {
        const sk = relay.streamKey;
        const streamKey = sk.substring(sk.lastIndexOf('/') + 1);
        const streamUrl = sk.substring(0, sk.lastIndexOf('/'));

        items.push({
          type: 'relay',
          enabled: relay.enabled,
          nickname: relay.label,
          relay_id: relay.id,
          streamKey,
          streamUrl,
          order: -1,
        });
      }

      destinations.value = items;
    });

    const enabledDestinations = computed(() => {
      return destinations.value.filter((item) => {
        if (item.type === 'platform') {
          return item.enabled && item.valid;
        }

        return item.enabled;
      });
    });

    const enabledPlatforms = computed(() => {
      const enabledPlatforms: RstToPlatformType[] = [];

      for (const destination of enabledDestinations.value) {
        if (destination.type === 'platform') {
          enabledPlatforms.push(destination.platform_type);
        }
      }

      return enabledPlatforms;
    });

    const isYouTubeEnabled = computed(() =>
      enabledPlatforms.value.includes(RstToPlatformType.youtube_account),
    );

    const isTikTokEnabled = computed(() =>
      enabledPlatforms.value.includes(RstToPlatformType.tiktok_account),
    );

    const isTikTokGrandfathered = computed(
      () => !!api.multistream.user.settings.data?.tiktokGrandfathered,
    );

    const canMultistreamWithoutUltra = computed(() => {
      return (
        enabledDestinations.value.length <= 2 &&
        isTikTokEnabled.value &&
        isTikTokGrandfathered.value
      );
    });

    const connectPlatform = async (platform: PlatformTypeToRst) => {
      // todo: what if no access_token?
      const token = auth.authResponseParams?.api_token?.access_token;
      const url = `https://streamlabs.com/slobs/merge/${token}/${platform}`;
      const u = new URL(url);
      console.debug('connectPlatform', url);

      if (window.slabsGlobal) {
        const port = (await auth.startResponseServer()) || 1234;

        u.searchParams.set('port', port.toString());

        openBrowserTo(u.toString());

        try {
          await auth.waitForQueryParams<{
            platform: string;
            platform_username: string;
            platform_token: string;
            platform_id: string;
          }>();

          api.multistream.user.platforms.refresh();
          // api.multistream.user.settings.refresh();
          api.multistream.user.profile.refresh();
          api.multistream.user.targets.refresh();
          api.platformInfo.refresh();

          await obs.bringToFront();
          await app.bringToFront();
        } catch (err) {
          console.error(err);
        }
      } else {
        await openBrowserTo(u.toString());
      }
    };

    tracking.on('stopped_streaming', async () => {
      stopPreviousBroadcast();
    });

    return {
      enableMultistream,
      disableMultistream,

      destinations,
      enabledDestinations,
      enabledPlatforms,
      platformConfigs,

      isYouTubeEnabled,
      isTikTokEnabled,
      isTikTokGrandfathered,
      canMultistreamWithoutUltra,

      connectPlatform,
    };
  },
  {
    persist: {
      storage: piniaPluginPersistedstate.localStorage(),
    },
  },
);
